import { logger } from "@/lib/logger";
import { createContext, useContext, useEffect, useState, ReactNode } from "react";
import { useAuth } from "./AuthContext";
import { supabase } from "@/integrations/supabase/client";

export interface Organization {
  id: string;
  name: string;
  type: string;
  is_solo_practitioner?: boolean;
  is_demo?: boolean;
  website?: string | null;
  phone?: string | null;
  address?: string | null;
  city?: string | null;
  state?: string | null;
  country?: string | null;
  postal_code?: string | null;
  created_at: string;
  updated_at: string;
}

export interface OrganizationMembership {
  organization: Organization;
  role: string;
}

interface OrganizationContextType {
  activeOrganization: Organization | null;
  setActiveOrganization: (org: Organization | null) => void;
  organizations: OrganizationMembership[];
  loading: boolean;
  refreshOrganizations: () => Promise<void>;
}

const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);

const ACTIVE_ORG_KEY = 'active_organization_id';

export function OrganizationProvider({ children }: { children: ReactNode }) {
  const { user, isAuthReady } = useAuth();
  const [activeOrganization, setActiveOrganizationState] = useState<Organization | null>(null);
  const [organizations, setOrganizations] = useState<OrganizationMembership[]>([]);
  const [loading, setLoading] = useState(true);

  const setActiveOrganization = (org: Organization | null) => {
    setActiveOrganizationState(org);
    if (org) {
      localStorage.setItem(ACTIVE_ORG_KEY, org.id);
    } else {
      localStorage.removeItem(ACTIVE_ORG_KEY);
    }
  };

  // Track if we've already tried to create an org to prevent infinite loops
  const [hasAttemptedCreate, setHasAttemptedCreate] = useState(false);

  const fetchOrganizations = async (skipAutoCreate = false) => {
    if (!user?.id) {
      setOrganizations([]);
      setActiveOrganizationState(null);
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await supabase
        .from("organization_members")
        .select(`
          role,
          organizations (*)
        `)
        .eq("user_id", user.id)
        .limit(50);

      if (error) throw error;

      const memberships: OrganizationMembership[] = (data || [])
        .filter(m => m.organizations)
        .map(m => ({
          organization: m.organizations as unknown as Organization,
          role: m.role
        }));

      setOrganizations(memberships);

      // Detect and warn about duplicate organizations
      const orgNames = memberships.map(m => m.organization.name);
      const duplicates = orgNames.filter((name, index) => orgNames.indexOf(name) !== index);
      if (duplicates.length > 0) {
        logger.warn("⚠️ Duplicate organizations detected:", duplicates);
      }

      // If no organizations exist and we haven't tried to create one yet, auto-create
      // CRITICAL: Only attempt once to prevent infinite loops
      if (memberships.length === 0 && !skipAutoCreate && !hasAttemptedCreate) {
        setHasAttemptedCreate(true);
        try {
          logger.debug("No organizations found, auto-creating (one-time attempt)...");
          const { data: orgId, error: createError } = await supabase.rpc(
            'auto_create_user_organization',
            { 
              p_user_id: user.id,
              p_organization_name: null,
              p_is_demo: user.email?.includes('+') || false
            }
          );

          if (createError) {
            logger.error("Error auto-creating organization:", createError);
          } else if (orgId) {
            // Wait a moment for RLS policies to catch up, then retry ONCE
            await new Promise(resolve => setTimeout(resolve, 500));
            await fetchOrganizations(true); // Skip auto-create on retry
            return;
          }
        } catch (err) {
          logger.error("Unexpected error during organization creation:", err);
        }
      }

      // Set active organization
      if (memberships.length > 0) {
        const storedOrgId = localStorage.getItem(ACTIVE_ORG_KEY);
        const storedOrg = memberships.find(m => m.organization.id === storedOrgId);
        
        if (storedOrg) {
          setActiveOrganizationState(storedOrg.organization);
        } else {
          setActiveOrganizationState(memberships[0].organization);
          localStorage.setItem(ACTIVE_ORG_KEY, memberships[0].organization.id);
        }
      }
    } catch (error) {
      logger.error("Error fetching organizations:", error);
    } finally {
      setLoading(false);
    }
  };

  const refreshOrganizations = async () => {
    setLoading(true);
    await fetchOrganizations();
  };

  // Reset the create attempt flag when user changes
  useEffect(() => {
    setHasAttemptedCreate(false);
  }, [user?.id]);

  useEffect(() => {
    if (isAuthReady) {
      fetchOrganizations();
    }
  }, [user?.id, isAuthReady]);

  return (
    <OrganizationContext.Provider 
      value={{ 
        activeOrganization, 
        setActiveOrganization, 
        organizations, 
        loading,
        refreshOrganizations 
      }}
    >
      {children}
    </OrganizationContext.Provider>
  );
}

export function useOrganization() {
  const context = useContext(OrganizationContext);
  if (context === undefined) {
    throw new Error("useOrganization must be used within an OrganizationProvider");
  }
  return context;
}
